const DEFAULT_OPTIONS = {
    threshold: 0,
    rootMargin: '0px',
    once: false,
};
/**
 * HCPViewportDetector
 *
 * A class that detects when elements enter or leave the viewport.
 * Uses the Intersection Observer API for performant visibility detection.
 *
 * @example
 * const viewportDetector = new HCPViewportDetector(document.querySelectorAll('.element, .other-element'), {
 *   onEnter: () => {
 *     console.log('The first element has entered the viewport');
 *   },
 *   onLeave: () => {
 *     console.log('All elements have left the viewport');
 *   },
 * });
 *
 */
class HCPViewportDetector {
    /**
     * Creates a new viewport detector
     * @param elements - Element(s) to observe (single element, array, or NodeList)
     * @param options - Configuration options
     */
    constructor(elements = null, options = {}) {
        this.observer = null;
        this.observedElements = new Set();
        this.visibilityState = new Map();
        this.onObserve = options.onObserve ?? null;
        this.onEnter = options.onEnter ?? null;
        this.onLeave = options.onLeave ?? null;
        this.threshold = options.threshold ?? DEFAULT_OPTIONS.threshold;
        this.rootMargin = options.rootMargin ?? DEFAULT_OPTIONS.rootMargin;
        this.once = options.once ?? DEFAULT_OPTIONS.once;
        this.initObserver();
        if (elements) {
            this.observeElements(elements);
        }
    }
    /**
     * Observes one or multiple elements
     * @param elements - A single element, array of elements, or NodeList
     */
    observeElements(elements) {
        if (elements instanceof Element) {
            this.observe(elements);
        }
        else {
            this.observeAll(elements);
        }
    }
    /**
     * Initializes the Intersection Observer
     */
    initObserver() {
        // Check if Intersection Observer is supported
        if (!('IntersectionObserver' in window)) {
            return;
        }
        this.observer = new IntersectionObserver((entries) => this.handleIntersection(entries), {
            rootMargin: this.rootMargin,
            threshold: this.threshold,
        });
    }
    /**
     * Handles intersection events
     */
    handleIntersection(entries) {
        entries.forEach((entry) => {
            const element = entry.target;
            const isVisible = entry.isIntersecting;
            const wasAnyVisible = this.isVisible();
            this.visibilityState.set(element, isVisible);
            const isAnyVisible = this.isVisible();
            if (isAnyVisible && !wasAnyVisible) {
                this.onEnter?.(element, entry);
                if (this.once) {
                    this.unobserveAll();
                }
            }
            if (!isAnyVisible && wasAnyVisible) {
                this.onLeave?.(element, entry);
            }
        });
    }
    /**
     * Starts observing an element for viewport visibility
     * @param element - The element to observe
     */
    observe(element) {
        if (!this.observer) {
            return;
        }
        if (this.observedElements.has(element)) {
            return;
        }
        this.observedElements.add(element);
        this.visibilityState.set(element, false);
        this.observer.observe(element);
        this.onObserve?.(element);
    }
    /**
     * Starts observing multiple elements
     * @param elements - The elements to observe
     */
    observeAll(elements) {
        elements.forEach((element) => this.observe(element));
    }
    /**
     * Stops observing an element
     * @param element - The element to stop observing
     */
    unobserve(element) {
        if (!this.observer) {
            return;
        }
        this.observer.unobserve(element);
        this.observedElements.delete(element);
        this.visibilityState.delete(element);
    }
    /**
     * Stops observing all elements
     */
    unobserveAll() {
        this.observedElements.forEach((element) => {
            this.observer?.unobserve(element);
        });
        this.observedElements.clear();
        this.visibilityState.clear();
    }
    /**
     * Checks if elements are currently being observed
     */
    isObserving() {
        return this.observedElements.size > 0;
    }
    /**
     * Returns whether any observed element is currently visible in viewport
     */
    isVisible() {
        for (const visible of this.visibilityState.values()) {
            if (visible) {
                return true;
            }
        }
        return false;
    }
    /**
     * Returns the number of elements being observed
     */
    getObservedCount() {
        return this.observedElements.size;
    }
    /**
     * Returns all observed elements
     */
    getObservedElements() {
        return Array.from(this.observedElements);
    }
    /**
     * Updates the callbacks
     */
    setCallbacks(callbacks) {
        if (callbacks.onObserve !== undefined) {
            this.onObserve = callbacks.onObserve;
        }
        if (callbacks.onEnter !== undefined) {
            this.onEnter = callbacks.onEnter;
        }
        if (callbacks.onLeave !== undefined) {
            this.onLeave = callbacks.onLeave;
        }
    }
    /**
     * Destroys the detector and cleans up all observers
     */
    destroy() {
        this.unobserveAll();
        this.observer?.disconnect();
        this.observer = null;
        this.onObserve = null;
        this.onEnter = null;
        this.onLeave = null;
    }
}
export { HCPViewportDetector, };
